import { Button, Grid, MenuItem, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { DEFAULT_DRAG_HANDLE_SELECTOR } from "../../../../app/constants";
import { useAppSelector } from "../../../../app/hooks";
import CommonTextField from "../../../../common/components/functional/commonTextField/CommonTextField";
import { CustomButton } from "../../../../common/components/functional/customButton/CustomButton";
import DialogContentWrapper from "../../../../common/components/functional/dialogs/DialogContentWrapper";
import OverlaySpinner from "../../../../common/components/functional/overlaySpinner/OverlaySpinner";
import { ActionButtonsContainer } from "../../../customer/styles";
import { EezyCompany } from "../../../invoices/invoiceAPI";

type Props = {
  eezyCompanies: EezyCompany[];
  closeDialog: () => void;
  handleSave: () => void;
  isNewPayslip?: boolean;
};

export const ManualPayslipDialogContent = ({
  eezyCompanies,
  closeDialog,
  handleSave,
  isNewPayslip = false,
}: Props) => {
  const { t } = useTranslation();
  const {
    control,
    formState: { isValid },
    getValues,
  } = useFormContext();

  const { status } = useAppSelector((state) => state.payslips);
  const isLoading = status === "pending";

  return (
    <DialogContentWrapper
      title={
        isNewPayslip
          ? t("talentProfile:addPayslip")
          : t("talentProfile:editPayslip")
      }
    >
      <OverlaySpinner isLoading={isLoading} />
      <Grid container item spacing={2} mt={0}>
        <Grid item xs={12} sx={{ display: "flex", alignItems: "center" }}>
          <Grid item xs={2}>
            <Typography variant="secondary">
              {t("payslips:eezyCompany")}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="eezyCompanyId"
              control={control}
              rules={{
                required: true,
              }}
              render={({ field: { value, onChange } }) => (
                <CommonTextField
                  value={value}
                  onChange={onChange}
                  select
                  fullWidth
                  disabled={!isNewPayslip}
                >
                  {eezyCompanies.map((eezyCompany) => (
                    <MenuItem
                      key={eezyCompany.eezyCompanyId}
                      value={eezyCompany.eezyCompanyId}
                    >
                      {eezyCompany.name}
                    </MenuItem>
                  ))}
                </CommonTextField>
              )}
            />
          </Grid>
        </Grid>
        <Grid item xs={12} container spacing={2}>
          <Grid item xs={3}>
            <Typography variant="secondary" fontSize={12}>
              {t("talentProfile:startDate")}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="secondary" fontSize={12}>
              {t("talentProfile:endDate")}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="secondary" fontSize={12}>
              {t("talentProfile:salaryDate")}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="secondary" fontSize={12}>
              {t("talentProfile:valueDate")}
            </Typography>
          </Grid>
        </Grid>
        <Grid item xs={12} container spacing={2}>
          <Grid item xs={3}>
            <Controller
              name="startDate"
              control={control}
              rules={{
                required: true,
              }}
              render={({ field: { value, onChange } }) => (
                <DatePicker
                  format="dd.MM.yyyy"
                  value={value}
                  onChange={(value: Date | null) => {
                    onChange(value);
                  }}
                  disabled={!isNewPayslip}
                  slotProps={{
                    textField: {
                      size: "small",
                    },
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={3}>
            <Controller
              name="endDate"
              control={control}
              rules={{
                required: true,
                validate: (value) =>
                  value && getValues("startDate")
                    ? getValues("startDate")?.getTime() <=
                      new Date(value).getTime()
                    : true,
              }}
              render={({ field: { value, onChange } }) => (
                <DatePicker
                  format="dd.MM.yyyy"
                  value={value}
                  onChange={(value: Date | null) => {
                    onChange(value);
                  }}
                  disabled={!isNewPayslip}
                  slotProps={{
                    textField: {
                      size: "small",
                      error:
                        value && getValues("startDate")
                          ? !(
                              getValues("startDate").getTime() <=
                              new Date(value).getTime()
                            )
                          : false,
                    },
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={3}>
            <Controller
              name="salaryDate"
              control={control}
              rules={{
                required: true,
                validate: (value) =>
                  value &&
                  new Date(value).getTime() >= new Date().setHours(0, 0, 0, 0),
              }}
              render={({ field: { value, onChange } }) => (
                <DatePicker
                  format="dd.MM.yyyy"
                  value={value}
                  onChange={(value: Date | null) => onChange(value)}
                  slotProps={{
                    textField: {
                      size: "small",
                      error:
                        value &&
                        !(
                          new Date(value).getTime() >=
                          new Date().setHours(0, 0, 0, 0)
                        ),
                    },
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={3}>
            <Controller
              name="valueDate"
              control={control}
              rules={{
                required: true,
              }}
              render={({ field: { value, onChange } }) => (
                <DatePicker
                  format="dd.MM.yyyy"
                  value={value}
                  onChange={(value: Date | null) => onChange(value)}
                  slotProps={{
                    textField: {
                      size: "small",
                    },
                  }}
                />
              )}
            />
          </Grid>
        </Grid>
        <Grid className={DEFAULT_DRAG_HANDLE_SELECTOR} item xs={12}>
          <ActionButtonsContainer>
            <CustomButton
              sx={{
                borderRadius: "18px",
                height: "36px",
              }}
              disabled={!isValid}
              variant="contained"
              onClick={handleSave}
              color="primary"
            >
              {t("common:save")}
            </CustomButton>
            <Button
              sx={{
                borderRadius: "18px",
                height: "36px",
              }}
              variant="outlined"
              onClick={closeDialog}
            >
              {t("common:cancel")}
            </Button>
          </ActionButtonsContainer>
        </Grid>
      </Grid>
    </DialogContentWrapper>
  );
};
